import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Grid, Typography, Button, styled, Theme } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import {
  setSelectedCrewDays,
  setSelectedCrewEvents,
  toggleSelectedCrewMemberAction,
  updateWaterfallPairings,
} from "src/redux/actions/CrewSchedule/CrewSchedule.Actions";
import { getBidPackage } from "src/pages/BidManager/helpers";
import {
  hasPermission,
  PolicyAction,
  PolicyResource,
  IBidPackageResponseListItem,
  IBidPackageResponse,
  convertSearchPairingToDisplayPairing,
  ISearchPairing,
} from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { useSelector, useDispatch } from "react-redux";
import {
  getBidPackageList,
  gotBidPackageList,
  setBidPackage as setLoadedBidPackage,
  resetBiddingTool,
  setAwardedBidsVisibility,
} from "src/redux/actions/Bidding.Actions";
import { SelectField } from "src/components/FormikComponents/SelectField";
import _ from "lodash";
import { CrewMemberBidding } from "src/components/CrewSchedule/CrewMemberBidding";
import { addStatusToBidPackages } from "src/pages/BidManager/BidPackageListWindow";
import { LoadingMessage } from "src/components/LoadingMessage/LoadingMessage";
import { BidPackageSummary } from "src/pages/BidManager/BidPackageSummary/BidPackageSummary";
import { CheckboxField } from "src/components/FormikComponents/CheckboxField";
import { IconButton } from "src/components/IconButton/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { IModalInfo } from "../CrewScheduleWindow";
import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { MosaicDirection } from "src/utils/MosaicTree";
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { openSnackbar } from "src/redux/actions/App.Actions";

interface ITimelineAddEvents {
  timelineContainer?: HTMLDivElement;
  windowId: string;
  path: string[];
  filteredCrew: ICrewMember[];
  bidAssignModeActive?: boolean;
  setActiveEventModal?: (modalInfo: IModalInfo) => void;
  setTabValue: React.Dispatch<React.SetStateAction<string | boolean>>;
}

/**
 * Disable selection of bid packages if their bidding window is open or their bidding block has started or if they are disabled.
 * @param option -
 * @returns
 */
const packageSelectionDisabled = (option: IBidPackageResponseListItem): boolean => {
  const now = new Date();
  return now <= new Date(option.bidWindowClose) || now >= new Date(option.bidBlockStart) || option.isDisabled;
};
const biddingActiveButtonColor = "#008080";

const OCCrewScheduleBidAssignments = styled(Grid)(({ theme }: { theme: Theme }) => ({
  display: "flex",
  padding: "10px",
  flexWrap: "wrap",
  "&.active": {
    "& .MuiButton-containedPrimary": {
      backgroundColor: `${biddingActiveButtonColor} !important`,
    },
    "& .MuiButton-outlinedPrimary": {
      color: `${biddingActiveButtonColor} !important`,
      borderColor: `${biddingActiveButtonColor} !important`,
    },
  },
  "& .bid_package_selector, & .crew_selector": {
    margin: "0px 10px",
    width: "350px",
    "& .input_control label": {
      width: "auto",
      margin: "0px 10px auto auto",
      ...theme.typography.subtitle1,
    },
  },
  "& .load_into_pairing_waterfall_btn": {
    fontSize: 12,
    margin: 0,
    color: theme.custom.forms.labels,
    width: "auto",
    "& button": {
      padding: "0px !important",
      margin: "5px !important",
    },
  },
  "& .toggle_awarded_bid_visibility_btn": {
    width: "auto",
    margin: 0,
    marginLeft: "10px",
    "& .label": { marginLeft: "5px !important" },
  },
  "& .bid_assignment_switch_wrapper": {
    display: "flex",
    "& .bid_assignment_switch": {
      width: "220px",
      height: "30px",
      margin: "2px",
      "&.activating": { width: "240px" },
      "& label": {
        margin: 0,
        width: "40px",
      },
      "& .switch-buttons": {
        marginLeft: 8,
        "& button": { padding: "0px 8px" },
      },
    },
  },

  "& .bid_assignment_text": {
    ...theme.typography.subtitle1,
    margin: "5px",
  },

  "& button.refresh_bid_packages_btn, & .load_bid_package_btn": {
    fontSize: "11px !important",
    borderRadius: "3px",
    height: "24px",
  },
}));

/**
 * return a formatted row for the bid package selector.
 * @param option -
 */
const formatBidPackageOption = (option: IBidPackageResponseListItem): JSX.Element => {
  switch (option.status) {
    case "DISABLED":
      return (
        <Grid
          container
          key={option._id}
          style={{ width: 210, fontSize: 10 }}
          wrap="nowrap"
          justifyContent="space-between"
        >
          <Typography variant="caption" style={{ marginRight: 10, fontSize: "inherit" }}>
            {option.title}
          </Typography>
          <Typography variant="caption" color="secondary" style={{ fontSize: "inherit" }}>
            DISABLED
          </Typography>
        </Grid>
      );
    case "Block Ended":
      return (
        <Grid
          container
          key={option._id}
          style={{ width: 210, fontSize: 10 }}
          wrap="nowrap"
          justifyContent="space-between"
        >
          <Typography variant="caption" style={{ marginRight: 10, fontSize: "inherit" }}>
            {option.title}
          </Typography>
          <Typography variant="caption" style={{ color: "MediumTurquoise", fontSize: "inherit" }}>
            Block Ended
          </Typography>
        </Grid>
      );
    case "Block Active":
      return (
        <Grid
          container
          key={option._id}
          style={{ width: 210, fontSize: 10 }}
          wrap="nowrap"
          justifyContent="space-between"
        >
          <Typography variant="caption" style={{ marginRight: 10, fontSize: "inherit" }}>
            {option.title}
          </Typography>
          <Typography variant="caption" style={{ color: "MediumTurquoise", fontSize: "inherit" }}>
            Block Active
          </Typography>
        </Grid>
      );
    case "Bidding Closed":
      return (
        <Grid
          container
          key={option._id}
          style={{ width: 210, fontSize: 10 }}
          wrap="nowrap"
          justifyContent="space-between"
        >
          <Typography variant="caption" style={{ marginRight: 10, fontSize: "inherit" }}>
            {option.title}
          </Typography>
          <Typography variant="caption" style={{ color: "Plum", fontSize: "inherit" }}>
            Bidding Closed
          </Typography>
        </Grid>
      );
    case "Bidding Open":
      return (
        <Grid
          container
          key={option._id}
          style={{ width: 210, fontSize: 10 }}
          wrap="nowrap"
          justifyContent="space-between"
        >
          <Typography variant="caption" style={{ marginRight: 10, fontSize: "inherit" }}>
            {option.title}
          </Typography>
          <Typography variant="caption" style={{ color: "Plum", fontSize: "inherit" }}>
            Bidding Open
          </Typography>
        </Grid>
      );
    case "Bidding Pending":
      return (
        <Grid
          container
          key={option._id}
          style={{ width: 210, fontSize: 10 }}
          wrap="nowrap"
          justifyContent="space-between"
        >
          <Typography variant="caption" style={{ marginRight: 10, fontSize: "inherit" }}>
            {option.title}
          </Typography>
          <Typography variant="caption" style={{ fontSize: "inherit" }}>
            Bidding Pending
          </Typography>
        </Grid>
      );
    default:
      return (
        <Grid
          container
          key={option._id}
          style={{ width: 210, fontSize: 10 }}
          wrap="nowrap"
          justifyContent="space-between"
        >
          <Typography variant="caption" style={{ marginRight: 10, fontSize: "inherit" }}>
            {option.title}
          </Typography>
          <Typography variant="caption" style={{ fontSize: "inherit" }}>
            Unknown
          </Typography>
        </Grid>
      );
  }
};

/**
 * CREW SCHEDULE BID ASSIGNMENT TAB
 * Allows users to select from available bid assignment packages and crew to assign bid pref ranks.
 */
export const CrewScheduleBidAssignment = (props: ITimelineAddEvents): JSX.Element => {
  const { windowId, path, filteredCrew, bidAssignModeActive, setActiveEventModal, setTabValue } = props;

  const dispatch = useDispatch();
  const theme = useTheme();
  const policies = useSelector((state: IRootState) => state.authentication?.attributes?.policies);

  const awardedBidsVisible = useSelector((state: IRootState) => state.bidding.awardedBidsVisible);
  const toggleAwardedBidsVisibility = useCallback((): void => {
    if (dispatch) dispatch(setAwardedBidsVisibility(!awardedBidsVisible));
  }, [awardedBidsVisible, dispatch]);

  const assignmentIsDirty = useSelector((state: IRootState) => state.bidding.assignmentIsDirty);

  // Bidding permissions checks.
  const canReadBidPackages = hasPermission(policies, PolicyResource.Bidding, PolicyAction.Read);
  const canAssignBids = hasPermission(policies, PolicyResource.Bidding, PolicyAction.Create);
  //const canUseUpdatePackage = hasPermission(policies, PolicyResource.Bidding, PolicyAction.Update);

  // Selected Crew Member
  const selectedCrew = useMemo(() => filteredCrew?.find((c) => c?.isSelected), [filteredCrew]);
  const toggleCrewMember = useCallback(
    (crewId: string) => {
      if (crewId) dispatch(toggleSelectedCrewMemberAction(crewId));
    },
    [dispatch]
  );

  // Reset the entire bidding tool if assignment mode is deactivated.
  const deactivateBidAssignment = useCallback(() => {
    dispatch(setSelectedCrewEvents([], false, false, false));
    dispatch(setSelectedCrewDays(null));
    dispatch(resetBiddingTool());
  }, [dispatch]);

  // Retrieve bid package list if not already present in redux.
  // If retrieving available packages, make certain to deselect any selected crew members.
  const [triedLoadingOnce, setTriedLoadingOnce] = useState<boolean>(false);
  const [isLoadingPackages, setIsLoadingPackages] = useState<boolean>(false);
  const allBidPackages = useSelector((state: IRootState) => state.bidding.bidPackages);
  const activeBidPackages = useMemo(() => allBidPackages?.filter((bp) => !bp.isDisabled), [allBidPackages]);

  // Actively loaded bid package.
  const selectedBidPackage = useSelector((state: IRootState) => state.bidding.selectedBidPackage);

  const [loadingPairingWaterfall, setLoadingPairingWaterfall] = useState<boolean>(false);
  const openPairingWaterfallWindow = useCallback(async (): Promise<void> => {
    setLoadingPairingWaterfall(true);
    //load in ONLY the pairings that are in the bid package
    const pIds = selectedBidPackage.bidPairings.map((bp) => bp.pairingId);
    const payload = {
      queryArgs: [{ condition: "EQ", field: "_id", value: pIds }],
      //@ts-ignore
      returnFields: [],
    };
    if (pIds.length > 0) {
      const searchResults = await AmplifyAPIWrapper.Post<ISearchPairing[]>("/pairings/search", payload, null);
      const displayPairings =
        Array.isArray(searchResults) && searchResults?.length
          ? searchResults.map((pairing: ISearchPairing) => convertSearchPairingToDisplayPairing(pairing))
          : [];

      if (Array.isArray(displayPairings) && displayPairings?.length) {
        const sortedDisplayPairings = displayPairings.sort(
          (a, b) => a.start?.valueOf() - b.start?.valueOf() || a.end?.valueOf() - b.end?.valueOf()
        );
        dispatch(updateWaterfallPairings(sortedDisplayPairings));
        const positionFilter: { [key: string]: any } = {};
        selectedBidPackage.bidPackage.positions.forEach((p) => {
          positionFilter[p] = "unfilled";
        });
        dispatch(
          openWindow(
            "Pairing Waterfall",
            "PairingWaterfallWindow",
            [],
            { biddingAssignmentMode: true, filter: positionFilter },
            "context",
            true,
            MosaicDirection.Top //Position of new tab
          )
        );
      }
    }
  }, [dispatch, selectedBidPackage]);

  const selectedBidPackageListItem = useMemo(() => {
    if (activeBidPackages?.length === 0 || !selectedBidPackage?.bidPackage?._id) {
      return null;
    }
    return activeBidPackages?.find((bp) => bp._id === selectedBidPackage?.bidPackage?._id);
  }, [activeBidPackages, selectedBidPackage?.bidPackage?._id]);

  const [bidPackage, setBidPackage] = useState<IBidPackageResponseListItem | null>(selectedBidPackageListItem);

  const getAllBidPackages = useCallback(async (): Promise<void> => {
    setIsLoadingPackages(true);
    setBidPackage(null);
    deactivateBidAssignment();
    if (selectedCrew?._id) toggleCrewMember(selectedCrew?._id);
    const response = await getBidPackageList();
    if (Array.isArray(response) && !!response?.length) {
      const bidPackageListWithStatus = addStatusToBidPackages(response);
      dispatch(gotBidPackageList(bidPackageListWithStatus));
    }
    setIsLoadingPackages(false);
  }, [dispatch, setBidPackage, toggleCrewMember, selectedCrew, deactivateBidAssignment]);

  // when component is mounted, attempt to load the bid packages once.
  useEffect(() => {
    if (allBidPackages?.length === 0 && !triedLoadingOnce) {
      setTriedLoadingOnce(true);
      getAllBidPackages();
    }
  }, [allBidPackages, getAllBidPackages, triedLoadingOnce]);

  const [activatingBidMode, setActivatingBidMode] = useState<boolean>(false);
  const activateBidAssignment = useCallback(
    async (packageId: string) => {
      setActivatingBidMode(true);
      const response: IBidPackageResponse = await getBidPackage(bidPackage?._id);
      if (response?.bidPackage?._id) {
        dispatch(setLoadedBidPackage(response));
      }
      setActivatingBidMode(false);
    },
    [bidPackage, dispatch]
  );

  // If selected bid package changes, make it the active package.
  useEffect(() => {
    if (bidPackage?._id) {
      deactivateBidAssignment();
      activateBidAssignment(bidPackage?._id);
    } else {
      deactivateBidAssignment();
    }
  }, [activateBidAssignment, bidPackage, deactivateBidAssignment]);

  // Track stage to better inform user on which steps to take to enter bid assignment mode.
  const stage: "selectbidpackage" | "activateBidAssignment" | "selectcrew" | "loadcrewbids" = useMemo(() => {
    switch (true) {
      case !bidPackage:
        return "selectbidpackage";
      case !!bidPackage && !bidAssignModeActive:
        return "activateBidAssignment";
      case !!bidPackage && bidAssignModeActive && !selectedCrew:
        return "selectcrew";
      default:
        return "loadcrewbids";
    }
  }, [bidPackage, selectedCrew, bidAssignModeActive]);

  if (!canReadBidPackages || !canAssignBids) {
    const warningMessage =
      !canReadBidPackages && !canAssignBids
        ? "You are unauthorized to view or assign crew bids, please see administrator for assistance."
        : canReadBidPackages && !canAssignBids
          ? "You are unauthorized to assign crew bids, please see administrator for assistance."
          : "You are unauthorized to view crew bids, please see administrator for assistance.";
    return (
      <Typography variant="body2" color="error">
        {warningMessage}
      </Typography>
    );
  }

  return (
    <OCCrewScheduleBidAssignments
      container
      className={`${bidAssignModeActive ? "active" : ""}`}
      direction="row"
      wrap="nowrap"
    >
      <Grid container direction="column" style={{ width: "auto", paddingRight: 15 }}>
        <Grid container className="bid_assignment_steps" alignItems="center">
          <IconButton style={{ paddingBottom: "4px", marginRight: "15px" }} onClick={() => setTabValue(false)}>
            <VisibilityOffIcon fontSize="small" />
          </IconButton>
          <Button
            id={"refresh_bid_packages_btn"}
            variant="contained"
            color="primary"
            disabled={isLoadingPackages || !canReadBidPackages}
            onClick={_.debounce(getAllBidPackages, 500)}
            className="refresh_bid_packages_btn"
            style={{ width: !activeBidPackages?.length ? 100 : 120 }}
          >
            {!activeBidPackages?.length ? "Load Packages" : "Refresh Packages"}
          </Button>
          <SelectField
            className="bid_package_selector"
            label="Bid Package"
            name="bidPackages"
            value={bidPackage}
            options={activeBidPackages}
            onChange={setBidPackage}
            isMulti={false}
            // isClearable={true}
            menuPosition="fixed"
            maxMenuHeight={400}
            getOptionValue={(bp: IBidPackageResponseListItem) => bp?._id}
            formatOptionLabel={formatBidPackageOption}
            getOptionLabel={(bp: IBidPackageResponseListItem) => {
              return `${bp.title}`;
            }}
            // Allow selection of all packages (even disabled)
            isOptionDisabled={(bp: IBidPackageResponseListItem, value: any) => false}
            disabled={!activeBidPackages?.length || isLoadingPackages}
            placeholder={
              isLoadingPackages ? "Loading Packages..." : !activeBidPackages?.length ? "Load Packages..." : "Select..."
            }
          />
        </Grid>
        {bidAssignModeActive && (
          <Grid container alignItems="center">
            <Grid container wrap="nowrap" className="load_into_pairing_waterfall_btn" alignItems="center">
              <IconButton color="primary" onClick={() => openPairingWaterfallWindow()} size="large">
                <OpenInNewIcon />
              </IconButton>
              <LoadingMessage
                message={
                  loadingPairingWaterfall
                    ? "Loading results into pairing waterfall"
                    : "Load bid package pairings into waterfall"
                }
                animate={loadingPairingWaterfall}
              />
            </Grid>
            <CheckboxField
              className="field checkboxfield toggle_awarded_bid_visibility_btn"
              label="Show Ineligible Bid Pairings"
              name="awarded_bid_visibility"
              checked={awardedBidsVisible}
              labelPlacement="end"
              onChange={() => toggleAwardedBidsVisibility()}
            />
          </Grid>
        )}
      </Grid>

      <Grid
        item
        className="bidding_change_status"
        style={{ borderLeft: "1px solid rgba(255,255,255,0.5)", padding: "0px 15px" }}
      >
        {selectedBidPackage && selectedCrew?._id ? (
          <CrewMemberBidding
            crewMember={selectedCrew}
            bidAssignmentMode={bidAssignModeActive}
            id={windowId}
            displayInHeader={true}
          />
        ) : selectedBidPackage && !selectedCrew?._id ? (
          <Grid container direction="column" style={{ width: "auto" }} wrap="nowrap">
            <BidPackageSummary
              id={windowId}
              path={path}
              bidPackage={selectedBidPackage?.bidPackage}
              bidPairingCount={selectedBidPackage?.bidPairings?.length ?? 0}
              crewCount={selectedBidPackage?.crewBids?.length ?? 0}
              hideTimeScale={true}
            />
            <Typography variant="caption" style={{ color: theme.typography.body1.color, margin: "0px 5px" }}>
              Select a crew member to assign bids
            </Typography>
          </Grid>
        ) : (
          <Grid item style={{ margin: 5 }}>
            <LoadingMessage
              animate={activatingBidMode}
              message={activatingBidMode ? "Loading Selected Package" : "Select Bid Package"}
            />
          </Grid>
        )}
      </Grid>

      {bidPackage?._id && (
        <IconButton
          style={{ position: "absolute", right: 5, top: 68, borderRadius: 3, padding: 0 }}
          onClick={() => {
            if (assignmentIsDirty) {
              setActiveEventModal({
                type: EventModalTypes.BidAssignmentDirty,
                modalProps: { message: "Please save or reset unsaved changes before closing bid package." },
              });
            } else {
              setBidPackage(null);
              deactivateBidAssignment();
            }
          }}
          size="large"
        >
          <CloseIcon />
        </IconButton>
      )}
    </OCCrewScheduleBidAssignments>
  );
};
